/**
 * Perth-timezone order-cycle logic — shared by CutoffCountdown, the
 * site-wide announcement bar, and any component that needs to know
 * whether we're before or after the weekly Thursday 11pm cut-off.
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: "Australia/Perth"` so the
 * math is always AWST-correct regardless of the visitor's browser
 * timezone (fixes the "shows the wrong countdown from Singapore" bug).
 *
 * States:
 *   BEFORE  — Sun 00:00 → Thu 23:00 Perth. Normal countdown toward Thu 11pm.
 *   AFTER   — Thu 23:00 → Sat 23:59 Perth. Missed-cutoff messaging.
 *   Sun 00:00+ resets to BEFORE (the "AFTER" window naturally rolls over).
 */

const PERTH_TZ = "Australia/Perth";

/** Parts of `now` as observed in Australia/Perth. Returns
 * `{ year, month, day, dayOfWeek, hour, minute, second }`.
 * dayOfWeek: 0 = Sunday … 6 = Saturday, matching JS Date convention. */
function nowInPerth() {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: PERTH_TZ,
    weekday: "short",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  const WEEKDAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    absMs: now.getTime(),
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    dayOfWeek: WEEKDAY_MAP[parts.weekday] ?? 0,
    hour: Number(parts.hour === "24" ? "0" : parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** Build a Date object representing "hour:minute Perth time" for a given
 *  Perth-local date (year/month/day). We do this by iterating: pick a
 *  candidate UTC ms, format it in Perth TZ, check if it matches the target
 *  wall-clock, adjust once (offsets don't shift by more than an hour). */
function makePerthDate(year, month, day, hour, minute = 0) {
  // Australia/Perth is a fixed UTC+8 (no DST since 2009), so the offset is
  // constant. We can trust a naïve UTC construction minus 8h.
  const ms = Date.UTC(year, month - 1, day, hour - 8, minute, 0, 0);
  return new Date(ms);
}

/** How many days from `dow` to reach `target` day-of-week (0-6),
 * with a minimum of 0 (same day). */
function daysUntilDow(fromDow, targetDow) {
  return (targetDow - fromDow + 7) % 7;
}

/** Format a Date as "Saturday 23 August" in Perth locale, no year. */
export function formatPerthDateLong(date) {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: PERTH_TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

/** Compute the next Thursday 23:00 Perth wall-clock from `now`. If we're
 *  currently after Thursday 23:00 Perth in the same week, returns *this*
 *  week's Thursday cut-off (the moment that just passed) — callers use
 *  `isPostCutoff()` to route to the missed-cutoff branch. */
function nextThursdayCutoff(pn) {
  // If today is Thu but we're already past 23:00, keep this Thursday as the
  // "just passed" reference; otherwise find the coming Thu.
  const isThu = pn.dayOfWeek === 4;
  const alreadyPast = isThu && pn.hour >= 23;
  const daysAhead = alreadyPast ? 0 : daysUntilDow(pn.dayOfWeek, 4);
  const target = makePerthDate(pn.year, pn.month, pn.day, 23, 0);
  return new Date(target.getTime() + daysAhead * 86400000);
}

/** Next Saturday (delivery day) in Perth wall-clock, as a Date. */
function nextSaturday(pn) {
  const days = daysUntilDow(pn.dayOfWeek, 6);
  const target = makePerthDate(pn.year, pn.month, pn.day, 8, 0);
  const daysAhead = days === 0
    // If it's Saturday today, we've missed the run this week — push to next Sat.
    ? 7
    : days;
  return new Date(target.getTime() + daysAhead * 86400000);
}

/** Are we currently in the missed-cutoff window?  Thursday 23:00 → Saturday 23:59 Perth.
 *  Sunday 00:00 resets to BEFORE. */
function isPostCutoff(pn) {
  // Thursday after 23:00
  if (pn.dayOfWeek === 4 && pn.hour >= 23) return true;
  // Friday all day
  if (pn.dayOfWeek === 5) return true;
  // Saturday all day
  if (pn.dayOfWeek === 6) return true;
  return false;
}

/** Break a millisecond duration into {days, hours, mins, secs}. */
function breakdown(ms) {
  if (ms <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
  const t = Math.floor(ms / 1000);
  return {
    days: Math.floor(t / 86400),
    hours: Math.floor((t % 86400) / 3600),
    mins: Math.floor((t % 3600) / 60),
    secs: t % 60,
  };
}

/**
 * The one function to rule the whole cycle. Returns:
 *  {
 *    mode: "countdown" | "post-cutoff",
 *    cutoff:              Date,   // Next Thursday 23:00 Perth
 *    delivery:            Date,   // Next Saturday delivery
 *    deliveryDateLabel:   string, // "Saturday 23 August"
 *    remaining: { days, hours, mins, secs }, // to `cutoff` (countdown mode only)
 *  }
 */
export function getOrderCycle() {
  const pn = nowInPerth();
  const post = isPostCutoff(pn);
  const delivery = nextSaturday(pn);
  const deliveryDateLabel = formatPerthDateLong(delivery);
  if (post) {
    return {
      mode: "post-cutoff",
      cutoff: nextThursdayCutoff(pn),
      delivery,
      deliveryDateLabel,
      remaining: { days: 0, hours: 0, mins: 0, secs: 0 },
    };
  }
  const cutoff = nextThursdayCutoff(pn);
  return {
    mode: "countdown",
    cutoff,
    delivery,
    deliveryDateLabel,
    remaining: breakdown(cutoff.getTime() - pn.absMs),
  };
}
